import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import SlidingTabs from '../components/SlidingTabs';
import IdentityInbox from '../components/IdentityInbox';
import { RegistrationStatusBadge, RunStatusBadge } from '../components/AccountStatusBadges';
import CompactBrowserViewer from '../components/CompactBrowserViewer';
import RunRecording from '../components/RunRecording';
import { useRunStream } from '../hooks/useRunStream';
import {
  useSente,
  SenteIdentity,
  SenteRegistration,
  SenteRunSummary,
  SenteAuditEvent,
  SenteUsage,
  SenteApiError,
} from '../hooks/useSente';

const LIVE_RUN_STATUSES = ['queued', 'running', 'awaiting_verification', 'blocked'];
// A gateway session is attachable (streamable) only once the browser is up — 'queued' has none yet.
const STREAMABLE = ['running', 'awaiting_verification', 'blocked'];

type TabId = 'accounts' | 'runs' | 'emails' | 'security';

const appHost = (appUrl: string) => {
  try {
    return new URL(appUrl).host;
  } catch {
    return appUrl;
  }
};

// Most-recent activity first: order by the latest run's time, falling back to when the
// account was created (an account with no run yet sinks to the bottom).
const activityTime = (reg: SenteRegistration) =>
  new Date(reg.latestRun?.createdAt ?? reg.createdAt).getTime();

// Why a run stopped, in the words of someone who has to decide whether to try that app again.
// A user who burns their one run on a target that structurally can't work (x.com wants a phone)
// and sees only `PHONE_REQUIRED` learns nothing and doesn't come back.
const FAILURE_HINTS: Record<string, string> = {
  PHONE_REQUIRED: 'The app demanded a phone number — it cannot be signed up for with email alone.',
  CAPTCHA_REQUIRED: 'A CAPTCHA stood in the way; take over the live view to clear it.',
  PAYMENT_REQUIRED: 'The app asked for a card before it would finish signup.',
  MISSING_FIELD: 'The form needed something the agent could not fill on its own.',
  LOGIN_UNCONFIRMED: 'The agent could not confirm it ended up signed in.',
  MFA_REQUIRED: "A code went to the owner's own email or phone — only a human can enter that one.",
  TOTP_REQUIRED: 'The account uses an authenticator app — vault its TOTP seed and re-login needs no human.',
  BLOCKED_TIMEOUT: 'The run waited for a human and nobody arrived in time.',
  AUTOMATION_STUCK: 'The agent stopped making progress on the page.',
  APP_UNREACHABLE: 'The app URL did not load.',
  ABORTED: 'Stopped by a person.',
  TASK_FAILED: 'The browser task ended without completing.',
};

// Audit actions in human units. Nobody trusts us with a password because of the string
// `credentials.read` — the sentence next to it is the whole point of this section.
const AUDIT_COPY: Record<string, { label: string; why: string; sensitive?: boolean }> = {
  'credentials.read': {
    label: 'Credentials read',
    why: "An account's username and password were revealed in plaintext.",
    sensitive: true,
  },
  'credentials.write': {
    label: 'Credentials updated',
    why: 'New credentials were written into the vault for this account.',
    sensitive: true,
  },
  'session.open': {
    label: 'Logged-in session opened',
    why: 'A live browser, already signed in to the account, was handed to a caller.',
    sensitive: true,
  },
  'session.close': { label: 'Logged-in session closed', why: 'That live browser session was torn down.' },
  'session.export': {
    label: 'Logged-in session exported',
    why: 'Cookies and storage left Sente as a portable session file.',
    sensitive: true,
  },
  'connection.create': {
    label: 'Account connected',
    why: 'An account you own was delegated to an identity; its credentials were vaulted.',
  },
  'connection.revoke': {
    label: 'Connection revoked',
    why: 'Delegated access was withdrawn — the agent can no longer log in.',
    sensitive: true,
  },
  'connection.delete': {
    label: 'Connection deleted',
    why: 'The stored credentials for that account were purged.',
    sensitive: true,
  },
  'registration.create': { label: 'Account registered', why: 'Sente signed up for a new account at this app.' },
  'run.intervene': { label: 'Human took over a run', why: 'A person took manual control of the browser mid-run.' },
  'run.resume': { label: 'Run resumed', why: 'Control was handed back to the agent.' },
  'run.abort': { label: 'Run aborted', why: 'A person stopped the run.' },
};

// Future actions we don't have copy for must still read as English — never a raw enum, never blank.
const auditCopy = (action?: string | null) => {
  const known = action ? AUDIT_COPY[action] : undefined;
  if (known) return known;
  const words = (action ?? '').replace(/[._-]+/g, ' ').trim();
  return { label: words ? words[0].toUpperCase() + words.slice(1) : 'Unknown event', why: '', sensitive: false };
};

// One account row. Owns its own live-view stream so a running account plays inline — click the
// video (or the card) to open the full run view with take-over + inbox.
const ActivityCard: React.FC<{
  reg: SenteRegistration;
  email: string;
  busy: boolean;
  onOpen: () => void;
  onStart: (e: React.MouseEvent) => void;
  onResume: (runId: string) => Promise<void>;
}> = ({ reg, email, busy, onOpen, onStart, onResume }) => {
  const run = reg.latestRun;
  const liveRun = Boolean(run && LIVE_RUN_STATUSES.includes(run.status));
  const streaming = Boolean(run && STREAMABLE.includes(run.status) && run.gatewaySessionId);
  const { screenshotUrl, currentUrl, isConnected } = useRunStream(run?.gatewaySessionId, streaming);
  // Recording is lazy — only fetch it when the user asks (keeps a long Activity list light).
  const [showReplay, setShowReplay] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  // Resume copy mirrors the run page so a blocked run reads the same in both places.
  const resumeLabel =
    run?.error?.code === 'SUBMIT_CONFIRMATION_REQUIRED'
      ? "I've submitted — resume"
      : run?.error?.code === 'MANUAL_INTERVENTION'
        ? 'Done — resume the agent'
        : "I've cleared it — resume";

  return (
    <div
      onClick={() => run && onOpen()}
      className={`p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-700 transition-colors ${run ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {appHost(reg.appUrl)}
            </h3>
            <RegistrationStatusBadge status={reg.status} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-slate-400">
            <Link
              to={`/identities/${reg.identityId}`}
              onClick={e => e.stopPropagation()}
              className="font-mono truncate hover:underline"
            >
              {email}
            </Link>
            {reg.username && <span>user: <span className="font-mono">{reg.username}</span></span>}
            <span className="text-xs text-gray-500 dark:text-slate-500">
              Created {new Date(reg.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {run && (
            <div className="text-right">
              <RunStatusBadge status={run.status} />
              <div className="text-xs text-gray-500 dark:text-slate-500 mt-1 capitalize">
                {run.type} run{run.error ? ` · ${run.error.code}` : ''}
              </div>
            </div>
          )}
          {/* Terminal run → offer its recorded video inline (lazy). */}
          {run && !liveRun && (
            <button
              onClick={e => { e.stopPropagation(); setShowReplay(s => !s); }}
              className="px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              {showReplay ? 'Hide' : 'Replay'}
            </button>
          )}
          {liveRun ? (
            <button
              onClick={e => { e.stopPropagation(); onOpen(); }}
              className={`px-4 py-2 text-white rounded-lg font-medium transition-colors text-sm ${
                run && run.status === 'blocked'
                  ? 'bg-amber-500 hover:bg-amber-600'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {run && run.status === 'blocked' ? 'Take over' : 'Watch'}
            </button>
          ) : (
            <button
              onClick={onStart}
              disabled={busy}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-900 dark:text-white rounded-lg font-medium transition-colors text-sm disabled:opacity-50"
            >
              {busy ? 'Starting…' : reg.status === 'active' ? 'Log in' : 'Retry'}
            </button>
          )}
        </div>
      </div>

      {/* Live browser inline. While BLOCKED, the agent pauses and its JPEG screencast freezes — so we
          swap in the interactive live view (the same one the run page shows) right here, so you can
          take control from the list instead of only after clicking through. Running/awaiting_verification
          keep the watch-only screencast. stopPropagation so interacting doesn't navigate the card. */}
      {run && run.status === 'blocked' && run.liveViewUrl ? (
        <div className="mt-4" onClick={e => e.stopPropagation()}>
          <iframe
            src={run.liveViewUrl}
            title="Interactive live view"
            allow="clipboard-read; clipboard-write"
            className="w-full aspect-video rounded-xl border-2 border-amber-300 dark:border-amber-700/60 shadow-sm"
          />
          {/* Hand control back to the agent, right here — no need to open the run page. */}
          <button
            onClick={async () => { setIsResuming(true); try { await onResume(run.id); } finally { setIsResuming(false); } }}
            disabled={isResuming}
            className="mt-3 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg font-semibold shadow-sm transition-colors text-sm"
          >
            {isResuming ? 'Resuming…' : resumeLabel}
          </button>
        </div>
      ) : streaming ? (
        <div className="mt-4" onClick={e => e.stopPropagation()}>
          <CompactBrowserViewer
            screenshotUrl={screenshotUrl}
            currentUrl={currentUrl}
            pageTitle=""
            projectName={appHost(reg.appUrl)}
            isConnected={isConnected}
            heightClass="aspect-video"
          />
        </div>
      ) : null}

      {/* Recorded video of a finished run (lazy, on Replay). */}
      {showReplay && run && (
        <div className="mt-4" onClick={e => e.stopPropagation()}>
          <RunRecording runId={run.id} />
        </div>
      )}
    </div>
  );
};

// used / cap with a bar. `cap` omitted → just the count (accounts has no cap).
const UsageMeter: React.FC<{ label: string; used: number; cap?: number; note?: string }> = ({
  label,
  used,
  cap,
  note,
}) => {
  const pct = cap ? Math.min((used / cap) * 100, 100) : 0;
  const bar = cap && used >= cap ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-blue-600';
  return (
    <div className="flex-1 min-w-[160px]">
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold text-gray-900 dark:text-white">{used}</span>
        {cap !== undefined && <span className="text-sm text-gray-500 dark:text-slate-400">of {cap}</span>}
        <span className="text-sm text-gray-500 dark:text-slate-400">{label}</span>
      </div>
      {cap !== undefined && (
        <div className="mt-2 h-1.5 bg-gray-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {note && <div className="mt-1.5 text-xs text-gray-500 dark:text-slate-500">{note}</div>}
    </div>
  );
};

// Activity — the cross-identity audit / monitoring page: what the agents are doing right now
// (accounts + runs), what mail moved through the identities, and who touched the credentials.
const Activity: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const {
    ready,
    listRegistrations,
    listIdentities,
    listRuns,
    listAuditEvents,
    getUsage,
    createRegistration,
    loginRegistration,
    resumeRun,
  } = useSente();

  const [registrations, setRegistrations] = useState<SenteRegistration[]>([]);
  const [identities, setIdentities] = useState<SenteIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<React.ReactNode>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [tab, setTab] = useState<TabId>('accounts');
  const [identityFilter, setIdentityFilter] = useState(''); // '' = all identities

  // Each of these loads on demand (usage on mount; runs/audit on first visit to their tab) and
  // degrades on its own — one dead endpoint must not take the page down.
  const [usage, setUsage] = useState<SenteUsage | null>(null);
  const [runs, setRuns] = useState<SenteRunSummary[] | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [runCodeFilter, setRunCodeFilter] = useState('');
  const [events, setEvents] = useState<SenteAuditEvent[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [regs, ids] = await Promise.all([listRegistrations(), listIdentities()]);
      setRegistrations(regs);
      setIdentities(ids);
    } catch (e: any) {
      setError(e.message || 'Failed to load activity');
    } finally {
      setIsLoading(false);
    }
  }, [listRegistrations, listIdentities]);

  useEffect(() => {
    if (ready) refresh();
  }, [ready, refresh]);

  // Usage is a header strip — a failure here just hides the strip, it never errors the page.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    getUsage()
      .then(u => { if (!cancelled) setUsage(u); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ready, getUsage]);

  useEffect(() => {
    if (!ready || tab !== 'runs') return;
    let cancelled = false;
    setRuns(null);
    setRunsError(null);
    listRuns({ identityId: identityFilter || undefined, limit: 200 })
      .then(r => { if (!cancelled) setRuns(r); })
      .catch((e: any) => { if (!cancelled) { setRuns([]); setRunsError(e.message || 'Failed to load runs'); } });
    return () => { cancelled = true; };
  }, [ready, tab, identityFilter, listRuns]);

  useEffect(() => {
    if (!ready || tab !== 'security') return;
    let cancelled = false;
    setEvents(null);
    setEventsError(null);
    listAuditEvents({ limit: 200 })
      .then(ev => { if (!cancelled) setEvents(ev); })
      .catch((e: any) => { if (!cancelled) { setEvents([]); setEventsError(e.message || 'Failed to load audit events'); } });
    return () => { cancelled = true; };
  }, [ready, tab, listAuditEvents]);

  const emailById = useMemo(
    () => new Map(identities.map(i => [i.id, i.email])),
    [identities],
  );

  const visibleRegs = useMemo(
    () =>
      [...registrations]
        .filter(r => !identityFilter || r.identityId === identityFilter)
        .sort((a, b) => activityTime(b) - activityTime(a)),
    [registrations, identityFilter],
  );

  // Runs waiting on a human right now. Derived from the accounts list, which already carries each
  // account's latest run — a blocked run is by definition the latest one for its account.
  // ponytail: an `instruct` run has no account and so can't surface here; move to
  // listRuns({ status: 'blocked' }) if instruct runs ever ship.
  const blocked = useMemo(
    () => visibleRegs.filter(r => r.latestRun?.status === 'blocked'),
    [visibleRegs],
  );

  const shownRuns = useMemo(
    () => (runs ?? []).filter(r => !runCodeFilter || r.error?.code === runCodeFilter),
    [runs, runCodeFilter],
  );

  // Failure codes, most frequent first — which targets keep refusing, at a glance.
  const failureCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of runs ?? []) {
      if (r.status !== 'failed' || !r.error?.code) continue;
      counts[r.error.code] = (counts[r.error.code] ?? 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [runs]);

  const runLimitError = (e: any): React.ReactNode =>
    e instanceof SenteApiError && e.code === 'RUN_LIMIT_EXCEEDED'
      ? 'Daily run limit reached for your org — try again tomorrow.'
      : e.message || 'Something went wrong';

  // Resume a blocked run after the operator cleared it inline in the live view, then refresh the list.
  const resume = async (runId: string) => {
    try {
      await resumeRun(runId);
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Failed to resume');
    }
  };

  // Log in (active account) or retry a failed registration — both land on the run page.
  const startRun = async (e: React.MouseEvent, reg: SenteRegistration) => {
    e.stopPropagation();
    setBusyId(reg.id);
    setError(null);
    try {
      const { run } =
        reg.status === 'active'
          ? await loginRegistration(reg.id)
          : await createRegistration({ identityId: reg.identityId, appUrl: reg.appUrl });
      navigate(`/runs/${run.id}`);
    } catch (err: any) {
      setError(runLimitError(err));
      setBusyId(null);
    }
  };

  const spinner = (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="h-full overflow-hidden flex flex-col bg-white dark:bg-slate-950">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs items={[{ label: 'Activity' }]} className="mb-6" />

            {/* Header + identity filter */}
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap" data-tour="activity-header">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Activity</h1>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Everything your agents did: accounts and live runs, every email in and out, and who touched
                  the credentials. Start a new account from an{' '}
                  <Link to="/" className="underline font-medium">identity</Link>.
                </p>
              </div>
              <select
                value={identityFilter}
                onChange={e => setIdentityFilter(e.target.value)}
                className="px-3 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-full"
              >
                <option value="">All identities</option>
                {identities.map(i => (
                  <option key={i.id} value={i.id}>{i.email}</option>
                ))}
              </select>
            </div>

            {/* Usage vs caps for the current period. */}
            {usage && (
              <div className="mb-6 p-5 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 flex flex-wrap gap-6">
                <UsageMeter
                  label="runs this period"
                  used={usage.runs.used}
                  cap={usage.runs.cap}
                  note={`Plan: ${usage.planId} · resets ${new Date(usage.emails.periodEnd).toLocaleDateString()}`}
                />
                <UsageMeter label="emails sent" used={usage.emails.used} cap={usage.emails.cap} />
                <UsageMeter label="identities" used={usage.identities.used} cap={usage.identities.cap} />
                <UsageMeter label="active accounts" used={usage.accounts.used} />
              </div>
            )}

            {/* Needs attention — only when something is actually waiting on a human. */}
            {blocked.length > 0 && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-3">
                  Needs you — {blocked.length} run{blocked.length > 1 ? 's' : ''} paused
                </h2>
                <div className="space-y-2">
                  {blocked.map(reg => {
                    const run = reg.latestRun!;
                    const code = run.error?.code;
                    return (
                      <div key={run.id} className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium text-gray-900 dark:text-white">{appHost(reg.appUrl)}</span>
                        <span className="text-sm font-mono text-gray-600 dark:text-slate-400 truncate max-w-[220px]">
                          {emailById.get(reg.identityId) ?? reg.identityId}
                        </span>
                        <span className="text-sm text-amber-800 dark:text-amber-300 min-w-0 flex-1">
                          {(code && FAILURE_HINTS[code]) || 'The agent paused and needs a person in the live view.'}
                        </span>
                        <button
                          onClick={() => navigate(`/runs/${run.id}`)}
                          className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors text-sm flex-shrink-0"
                        >
                          Take over
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            <SlidingTabs
              tabs={[
                { id: 'accounts', label: 'Accounts', badge: visibleRegs.length },
                { id: 'runs', label: 'Runs' },
                { id: 'emails', label: 'Emails' },
                { id: 'security', label: 'Security' },
              ]}
              activeTab={tab}
              onTabChange={t => setTab(t as TabId)}
            />

            {tab === 'accounts' && (
              isLoading ? spinner : visibleRegs.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800">
                  <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No accounts yet</h3>
                  <p className="text-gray-500 dark:text-slate-400 mb-4">
                    Open an identity and register it with an app — the managed browser signs up, completes email
                    verification, and the run shows up here.
                  </p>
                  <Link
                    to="/"
                    className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Go to Identities
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {visibleRegs.map(reg => (
                    <ActivityCard
                      key={reg.id}
                      reg={reg}
                      email={emailById.get(reg.identityId) ?? reg.identityId}
                      busy={busyId === reg.id}
                      onOpen={() => reg.latestRun && navigate(`/runs/${reg.latestRun.id}`)}
                      onStart={e => startRun(e, reg)}
                      onResume={resume}
                    />
                  ))}
                </div>
              )
            )}

            {tab === 'runs' && (
              <div>
                {runsError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                    {runsError}
                  </div>
                )}
                {/* Failures grouped by code — which targets keep refusing, and why. */}
                {failureCounts.length > 0 && (
                  <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Failures:</span>
                    {failureCounts.map(([code, n]) => (
                      <button
                        key={code}
                        onClick={() => setRunCodeFilter(c => (c === code ? '' : code))}
                        title={FAILURE_HINTS[code] || code}
                        className={`px-3 py-1 text-xs font-medium rounded-full font-mono transition-colors ${
                          runCodeFilter === code
                            ? 'bg-red-600 text-white'
                            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40'
                        }`}
                      >
                        {code} · {n}
                      </button>
                    ))}
                    {runCodeFilter && (
                      <button
                        onClick={() => setRunCodeFilter('')}
                        className="text-xs text-gray-500 dark:text-slate-400 hover:underline"
                      >
                        clear
                      </button>
                    )}
                  </div>
                )}
                {runCodeFilter && FAILURE_HINTS[runCodeFilter] && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg text-sm text-gray-700 dark:text-slate-300">
                    <span className="font-mono font-medium">{runCodeFilter}</span> — {FAILURE_HINTS[runCodeFilter]}
                  </div>
                )}

                {runs === null ? spinner : shownRuns.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400">
                    No runs match.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-slate-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
                    {shownRuns.map(run => (
                      <div
                        key={run.id}
                        onClick={() => navigate(`/runs/${run.id}`)}
                        className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors cursor-pointer flex-wrap"
                      >
                        <span className="text-xs text-gray-500 dark:text-slate-500 w-[150px] flex-shrink-0">
                          {new Date(run.createdAt).toLocaleString()}
                        </span>
                        <span className="text-xs font-medium text-gray-600 dark:text-slate-400 capitalize w-[64px] flex-shrink-0">
                          {run.type}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                          {run.appUrl ? appHost(run.appUrl) : '—'}
                        </span>
                        <span className="text-sm font-mono text-gray-500 dark:text-slate-400 truncate max-w-[220px]">
                          {run.identityEmail}
                        </span>
                        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                          {run.error?.code && (
                            <span
                              title={FAILURE_HINTS[run.error.code] || run.error.detail || ''}
                              className="px-2 py-0.5 text-xs font-mono rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                            >
                              {run.error.code}
                            </span>
                          )}
                          <RunStatusBadge status={run.status} errorCode={run.error?.code} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Org-wide inbox — same component the identity page uses, unscoped. */}
            {tab === 'emails' && (
              <IdentityInbox
                identityId={identityFilter || null}
                email={emailById.get(identityFilter) ?? ''}
                emailById={emailById}
              />
            )}

            {tab === 'security' && (
              <div>
                <p className="mb-4 text-sm text-gray-500 dark:text-slate-400">
                  Every credential read, session hand-off and connection change, org-wide. Written by the API
                  and never edited. The identity filter doesn't apply here — some events are recorded against a
                  run or an account rather than an identity, and silently dropping those would make this
                  trail worse than useless.
                </p>
                {eventsError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                    {eventsError}
                  </div>
                )}
                {events === null ? spinner : events.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400">
                    Nothing has touched a credential or a session yet.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-slate-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
                    {events.map(ev => {
                      const copy = auditCopy(ev.action);
                      return (
                        <div key={ev.id} className="px-4 py-3 bg-white dark:bg-slate-900 flex items-start gap-3 flex-wrap">
                          <span
                            className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              copy.sensitive ? 'bg-amber-500' : 'bg-gray-300 dark:bg-slate-700'
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 dark:text-white">{copy.label}</span>
                              {ev.subjectType && (
                                <span className="text-xs font-mono text-gray-500 dark:text-slate-500">
                                  {ev.subjectType} {ev.subjectId}
                                </span>
                              )}
                            </div>
                            {copy.why && (
                              <div className="text-sm text-gray-600 dark:text-slate-400 mt-0.5">{copy.why}</div>
                            )}
                            {ev.meta && Object.keys(ev.meta).length > 0 && (
                              <div className="text-xs font-mono text-gray-500 dark:text-slate-500 mt-1 break-all">
                                {Object.entries(ev.meta).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-slate-500 flex-shrink-0">
                            {new Date(ev.createdAt).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activity;
